/**
 * usePlaceContext – single RPC call for the "Gör mer av det här" module.
 * Returns related events + nearby places from get_place_context RPC.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContextEvent {
  id: string;
  producer_id: string;
  place_id: string | null;
  title: string;
  timing_mode: string;
  start_date: string | null;
  end_date: string | null;
  price_ore: number;
  is_free: boolean;
  max_spots: number | null;
  booking_mode: string;
  venue_text: string | null;
  hero_media_asset_id: string | null;
  hero_image_path_640: string | null;
  hero_image_bucket: string | null;
}

export interface ContextPlace {
  id: string;
  title: string;
  slug: string;
  distance_km: number;
  intents: string[];
  hero_media_asset_id: string | null;
  hero_image_path_640: string | null;
  hero_image_bucket: string | null;
}

interface PlaceContextResult {
  place_id: string;
  radius_km: number;
  events: ContextEvent[];
  places: ContextPlace[];
}

export function usePlaceContext(placeId: string | undefined, radiusKm = 25) {
  const query = useQuery({
    queryKey: ["place-context", placeId, radiusKm],
    queryFn: async (): Promise<PlaceContextResult> => {
      const { data, error } = await supabase.rpc("get_place_context", {
        p_place_id: placeId!,
        p_radius_km: radiusKm,
      });

      if (error) {
        console.error("[usePlaceContext] RPC error:", error);
        throw error;
      }

      const result = data as unknown as PlaceContextResult;
      return {
        place_id: result.place_id,
        radius_km: result.radius_km,
        events: result.events ?? [],
        places: result.places ?? [],
      };
    },
    enabled: !!placeId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    events: query.data?.events ?? [],
    places: query.data?.places ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
